import { CircularProgress, Stack, Typography } from "@mui/material";
import { ReuseModal } from "../../ReuseModal";
import { ReuseInput } from "../../ReuseInput";
import { CustomButton } from "../../CustomButton";
import { PlusIcon, StarsOutlineIcon } from "../../../images";
import { useState } from "react";
import { useAddJobTitlesMutation } from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { ReuseTooltip } from "../../ReuseTooltip";
import { ButtonsColorType } from "../../../../types/enums";
import { ReuseButton } from "../../ReuseButton";

type Props = {
  campaign_id: string;
  onSuccessHandler: () => void;
  isDisabled: boolean;
};

export const AnalyzeJobTitlesModal = ({
  campaign_id,
  onSuccessHandler,
  isDisabled,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [addJobTitles, { isLoading }] = useAddJobTitlesMutation();

  const inputChange = (_: string, value: string) => {
    setText(value);
  };

  const openHandler = () => setIsOpen(true);
  const closeHandler = () => {
    setIsOpen(false);
    setText("");
  };

  const onSubmit = () => {
    addJobTitles({ campaign_id, text })
      .unwrap()
      .then(() => {
        onSuccessHandler();
        closeHandler();
      })
      .catch(() =>
        toast.error(
          <CustomToast
            type={"error"}
            text={`Something went wrong. Please try again`}
            title={"Failed to Analyze Data"}
          />,
        ),
      );
  };

  return (
    <>
      <ReuseTooltip
        maxWidth={"200px"}
        text={
          "Paste job titles or functions in free form. The system will analyze the" +
          " input and organize titles into the appropriate buying committee roles."
        }
      >
        <ReuseButton
          onClick={openHandler}
          buttonColor={ButtonsColorType.dark_orange}
          disabled={isDisabled}
          startIcon={<PlusIcon color={isDisabled ? "grey" : "#CB6D2B"} />}
        >
          Add Titles
        </ReuseButton>
      </ReuseTooltip>
      <ReuseModal
        isOpen={isOpen}
        onClose={closeHandler}
        title={"Add Titles"}
        submitBtnColor={"#CF3E00"}
        maxWidth={"680px"}
        isLoading={isLoading}
        isDisabledCancelBtn={isLoading}
        isDisabled={isLoading}
        withBtnBlock={false}
      >
        <Stack gap="32px">
          <Typography component="span" variant="body1" color="text.secondary">
            Paste job titles or functions in free form. The system will analyze
            the input and organize titles into the appropriate buying committee
            roles.
          </Typography>
          <ReuseInput
            value={text}
            errorMessage={""}
            label={"Job Titles or Functions"}
            name={"AI assistant"}
            placeholder={"Job Titles or Functions"}
            handleInputChange={inputChange}
            isError={false}
            setErrorHandler={() => {}}
            multiline
            maxRows={3}
            withResize={false}
            minHeight={"198px"}
          />
          <Stack direction="row" gap="8px">
            <CustomButton
              variant="contained"
              textColor="#4C545B"
              type="button"
              customColor="#F1F1F1"
              onClick={closeHandler}
              fullWidth
              sx={{ height: "40px" }}
              disabled={isLoading}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="outlined"
              customColor="#FFFFFF"
              textColor="#CB6D2B"
              type="button"
              fullWidth
              sx={{ borderColor: "#FE8836", height: "40px" }}
              onClick={onSubmit}
              disabled={!text || isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color={"inherit"} />
                ) : (
                  <StarsOutlineIcon color={text ? undefined : "#9B9B9B"} />
                )
              }
            >
              Analyze & Add
            </CustomButton>
          </Stack>
        </Stack>
      </ReuseModal>
    </>
  );
};
